/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, {
  useEffect, useState, useRef, useMemo,
} from 'react';
import styled from '@emotion/styled';
import { animated, useTransition } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import { useSelector, useDispatch } from 'react-redux';
import Text from '../../../../shared/components/Text/Text';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import {
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
  setReduxCalendarContainers,
  /** commenting out for now, might need later */
  /** setAnimatedSidebarContainerWidth, */
} from '../../ducks/calendarActions';
import ExercisesSideBarContent from './ExercisesSideBarContent';
import ProgramLibrarySideBarContent from './ProgramLibrarySideBarContent';
import TemplatesSideBarContent from './TemplatesSideBarContent';
import StatsSideBarContent from './StatsSideBarContent';
import CalendarCalendarPicker from './CalendarCalendarPicker';
import SideBarTitleWrapper from './SideBarTitleWrapper';
import PhoneSideBarContent from './PhoneSideBarContent';
import HistorySideBarContent from './HistorySideBarContent';
import {
  ICON_RAIL_WIDTH,
  SIDEBAR_WIDTH_DESKTOP,
  SIDEBAR_WIDTH_TABLET,
  SIDEBAR_TABLET_BREAKPOINT,
  SIDEBAR_DRAWER_BREAKPOINT,
} from '../../constants/sidebarDimensions';
import JournalSideBarContent from './JournalSideBarContent';

const SideBarWrapper = styled('div')`
  position: relative;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${(props) => props.isPrimaryAnimatedSideBarOpen && '16px'};
  box-shadow: 0px 4px 8px 0px #3031331A;
  background: white;
  transition: ${TransitionGlobal};
  overflow: hidden;
  min-width: ${(props) => (!props.isPrimaryAnimatedSideBarOpen ? '0%' : `${SIDEBAR_WIDTH_DESKTOP}px`)};
  max-width: ${SIDEBAR_WIDTH_DESKTOP}px;

  @media screen and (max-width: ${SIDEBAR_TABLET_BREAKPOINT}px) {
    min-width: ${(props) => (!props.isPrimaryAnimatedSideBarOpen ? '0%' : `${SIDEBAR_WIDTH_TABLET}px`)};
    max-width: ${(props) => (!props.isPrimaryAnimatedSideBarOpen ? '0%' : `${SIDEBAR_WIDTH_TABLET}px`)};
  }

  ${(props) => props.viewType === 'weekView' && 'position: sticky;'}
  ${(props) => props.viewType === 'weekView' && `left: ${ICON_RAIL_WIDTH}px;`}
  ${(props) => props.viewType === 'weekView' && 'z-index: 90;'}

  /* Small screens: opened from the header hamburger as a drawer beside the icon
     rail. Declared after the weekView rules so it wins for both view types. */
  @media screen and (max-width: ${SIDEBAR_DRAWER_BREAKPOINT}px) {
    min-width: ${(props) => (props.isPrimaryAnimatedSideBarOpen ? `calc(100vw - ${ICON_RAIL_WIDTH}px)` : '0%')};
    max-width: ${(props) => (props.isPrimaryAnimatedSideBarOpen ? `calc(100vw - ${ICON_RAIL_WIDTH}px)` : '0%')};

    ${(props) => {
    /** Week view keeps its shipped `sticky` positioning: the document itself
     * scrolls horizontally there, and sticky already pins the panel beside the
     * rail. Absolute would anchor it to the fit-content wrapper and scroll it
     * off-screen with the page. */
    if (!props.isPrimaryAnimatedSideBarOpen || props.viewType === 'weekView') {
      return '';
    }
    return `
      position: absolute;
      left: ${ICON_RAIL_WIDTH}px;
      top: 0;
      height: 100%;
      z-index: 100;
    `;
  }}
  }
`;

const AnimatedSideBarPrimary = () => {
  const dispatch = useDispatch();
  const [sideBarContainerHeight, setSideBarContainerHeight] = useState(0);
  const [sideBarContainerWidth, setSideBarContainerWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth || 0);
  const prevWindowWidthRef = useRef(windowWidth); /** ref to track the prev window width */
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const newPreSaveItemFromCreateExerciseForm = useSelector(
    (state) => state.calendar.data.newPreSaveItemFromCreateExerciseForm,
  );
  const allExercises = useSelector((state) => state.calendar.data.allExercises);
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount);

  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const sideBarDataTag = useSelector((state) => state.calendar.data.primarySideBarDataTag);
  const dragIsInProgress = useSelector((state) => state.calendar.data.dragIsInProgress);
  const programType = useSelector((state) => state.calendar.data.programType);

  const transitions = useTransition(sideBarDataTag ? [sideBarDataTag] : [], null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 1 },
    config: (state) => (state === 'leave' ? { duration: 0 } : { duration: 250 }),
  });

  const accessoryItemsList = [
    {
      name: 'Session Break',
      id: 'Session Break',
      icon: 'page-break',
      iconColor: '#9E9E9E',
      containerColor: 'white',
      borderColor: '#EEEEEE',
      type: 'session_break',
    },
    {
      name: 'Rest Time',
      id: 'Rest Time',
      icon: 'clock',
      iconColor: '#9E9E9E',
      containerColor: 'white',
      borderColor: '#EEEEEE',
      type: 'rest',
    },
    {
      name: 'Video',
      id: 'Video',
      icon: 'video-add',
      iconColor: '#9E9E9E',
      containerColor: 'white',
      borderColor: '#EEEEEE',
      type: 'video',
    },
  ];

  // Memoize filtering of exercises
  const liftExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'L'),
    [allExercises],
  );

  const warmUpExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'W'),
    [allExercises],
  );

  const SAQCExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'R'),
    [allExercises],
  );

  const CircuitExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'C'),
    [allExercises],
  );

  const NoteExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'N'),
    [allExercises],
  );

  const QuestionnaireExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'S'),
    [allExercises],
  );

  const CoolDownExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'CD'),
    [allExercises],
  );

  const HealthExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'H'),
    [allExercises],
  );

  const ProgressionExercises = useMemo(
    () => allExercises.filter((exercise) => exercise.type === 'P'),
    [allExercises],
  );

  const TagExercises = useMemo(() => tagsForAccount, [tagsForAccount]);

  const formattedLiftExercises = useMemo(
    () => liftExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'lift',
    })),
    [liftExercises],
  );

  const formattedWarmUpExercises = useMemo(
    () => warmUpExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'warmup',
    })),
    [warmUpExercises],
  );

  const formattedSAQCExercises = useMemo(
    () => SAQCExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'cardio',
    })),
    [SAQCExercises],
  );

  const formattedCircuitExercises = useMemo(
    () => CircuitExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'circuit',
    })),
    [CircuitExercises],
  );

  const formattedNoteExercises = useMemo(
    () => NoteExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'note',
    })),
    [NoteExercises],
  );

  const formattedQuestionnaireExercises = useMemo(
    () => QuestionnaireExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'science',
    })),
    [QuestionnaireExercises],
  );

  const formattedCoolDownExercises = useMemo(
    () => CoolDownExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'cooldown',
    })),
    [CoolDownExercises],
  );

  const formattedHealthExercises = useMemo(
    () => HealthExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'heart',
    })),
    [HealthExercises],
  );

  const formattedProgressionExercises = useMemo(
    () => ProgressionExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'chart',
    })),
    [ProgressionExercises],
  );

  const formattedTagExercises = useMemo(
    () => TagExercises.map((exercise) => ({
      ...exercise,
      id: String(exercise.id),
      icon: 'tag2',
      type: 'T',
    })),
    [TagExercises],
  );

  useEffect(() => {
    if (allExercises?.length) {
      dispatch(setReduxCalendarContainers({
        liftCalendarItems: formattedLiftExercises,
        warmUpCalendarItems: formattedWarmUpExercises,
        SAQCCalendarItems: formattedSAQCExercises,
        circuitCalendarItems: formattedCircuitExercises,
        noteCalendarItems: formattedNoteExercises,
        questionnaireCalendarItems: formattedQuestionnaireExercises,
        coolDownCalendarItems: formattedCoolDownExercises,
        healthCalendarItems: formattedHealthExercises,
        progressionCalendarItems: formattedProgressionExercises,
        tagCalendarItems: formattedTagExercises,
        accessoryCalendarItems: accessoryItemsList,
      }));
    }
  }, [allExercises]);

  useEffect(() => {
    if (newPreSaveItemFromCreateExerciseForm) {
      dispatch(setReduxCalendarContainers({
        liftCalendarItems: formattedLiftExercises,
        warmUpCalendarItems: formattedWarmUpExercises,
        SAQCCalendarItems: formattedSAQCExercises,
        circuitCalendarItems: formattedCircuitExercises,
        noteCalendarItems: formattedNoteExercises,
        questionnaireCalendarItems: formattedQuestionnaireExercises,
        coolDownCalendarItems: formattedCoolDownExercises,
        healthCalendarItems: formattedHealthExercises,
        progressionCalendarItems: formattedProgressionExercises,
        tagCalendarItems: formattedTagExercises,
        accessoryCalendarItems: accessoryItemsList,
      }));
    }
  }, [newPreSaveItemFromCreateExerciseForm]);

  /** Update window width on resize */
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const prevWindowWidth = prevWindowWidthRef?.current;

    /** Only close the sidebar if window width is less than 768 and has decreased */
    if (windowWidth < 768 && windowWidth < prevWindowWidth && isPrimaryAnimatedSideBarOpen) {
      dispatch(setIsPrimaryAnimatedSideBarOpen(false));
      dispatch(setPrimarySideBarDataTag(null));
    }

    /** Update previous window width */
    prevWindowWidthRef.current = windowWidth;
  }, [windowWidth, dispatch, isPrimaryAnimatedSideBarOpen]);

  const rafId = useRef(null);

  const scrollStep = () => {
    // Bigger scroll step for faster scrolling per frame
    window.scrollBy({ left: -10, behavior: 'auto' });
    rafId.current = requestAnimationFrame(scrollStep);
  };

  const handleMouseEnter = () => {
    if (!rafId.current) rafId.current = requestAnimationFrame(scrollStep);
  };

  const handleMouseLeave = () => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  };

  return (
    <SideBarWrapper
      isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
      windowWidth={windowWidth}
      viewType={viewType}
      onMouseEnter={viewType === 'weekView' && dragIsInProgress ? handleMouseEnter : () => {}}
      onMouseLeave={handleMouseLeave}
    >
      <ResizeObserver
        onResize={(rect) => {
          setSideBarContainerHeight(rect?.height);
          setSideBarContainerWidth(rect?.width);
          if (viewType === 'weekView') {
            /** commenting this out for now, might need later */
            /** dispatch(setAnimatedSidebarContainerWidth(rect.width)); */
          }
        }}
      />
      {isPrimaryAnimatedSideBarOpen && (
        <>
          {transitions.map(({ item, props }) => (
            item ? (
              <animated.div
                style={{
                  ...props, display: 'flex', flexDirection: 'column', gap: '16px', height: '100%',
                }}
                key={item}
              >
                {item === 'calendar2' && (
                <>
                  <SideBarTitleWrapper />
                  <CalendarCalendarPicker />
                </>
                )}
                {item === 'tag2' && <Text>{item}</Text>}
                {item === 'comments' && <Text>{item}</Text>}
                {item === 'templates' && (
                <TemplatesSideBarContent
                  sideBarContainerHeight={sideBarContainerHeight}
                  sideBarContainerWidth={sideBarContainerWidth}
                  windowWidth={windowWidth}
                />
                )}
                {item === 'lift' && (
                <ExercisesSideBarContent
                  sideBarContainerHeight={sideBarContainerHeight}
                  sideBarContainerWidth={sideBarContainerWidth}
                  windowWidth={windowWidth}
                />
                )}
                {item === 'note' && (
                <ProgramLibrarySideBarContent />
                )}
                {item === 'journal' && (
                <JournalSideBarContent sideBarContainerHeight={sideBarContainerHeight} />
                )}
                {item === 'gs-chart' && (
                <StatsSideBarContent />
                )}
                {item === 'phone' && (
                <PhoneSideBarContent />
                )}
                {item === 'history' && programType !== 'free-form-program' && (
                <HistorySideBarContent />
                )}
              </animated.div>
            ) : null

          ))}
        </>
      )}
    </SideBarWrapper>
  );
};

export default AnimatedSideBarPrimary;
